import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Upload, Edit } from "lucide-react";
import { PerformanceList } from "./AdminPerformance/PerformanceList";
import { PerformanceImport } from "./AdminPerformance/PerformanceImport";

interface CampaignPerformance {
  id: string;
  campaign_id: string;
  campaign_name: string;
  media_owner_name: string;
  start_date: string;
  end_date: string;
  total_budget: number;
  actual_reach: number;
  actual_engagement: number;
  actual_spent: number;
  performance_updated_at: string | null;
  status: string;
}

const AdminPerformance = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  const checkAdminAccess = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
      
      if (error) throw error;
      
      if (!data) {
        toast.error(language === 'ar' ? 'غير مصرح لك بالوصول' : 'Unauthorized access');
        navigate('/');
        return;
      }
      
      setIsAdmin(true);
      fetchCampaigns();
    } catch (error) {
      console.error('Error checking admin access:', error);
      toast.error(language === 'ar' ? 'خطأ في التحقق من الصلاحيات' : 'Error verifying permissions');
      navigate('/');
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('saved_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(language === 'ar' ? 'فشل في جلب البيانات' : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePerformance = async (
    campaignId: string,
    reach: number,
    engagement: number,
    spent: number
  ) => {
    try {
      const { error } = await supabase
        .from('saved_campaigns')
        .update({
          actual_reach: reach,
          actual_engagement: engagement,
          actual_spent: spent,
          performance_updated_at: new Date().toISOString(),
          performance_updated_by: user?.id
        })
        .eq('id', campaignId);

      if (error) throw error;

      toast.success(language === 'ar' ? 'تم تحديث البيانات بنجاح' : 'Performance data updated successfully');
      fetchCampaigns();
    } catch (error) {
      console.error('Error updating performance:', error);
      toast.error(language === 'ar' ? 'فشل في تحديث البيانات' : 'Failed to update performance data');
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'ar' ? 'رجوع' : 'Back'}
          </Button>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">
              {language === 'ar' ? 'إدارة أداء الحملات' : 'Campaign Performance Management'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="list" className="gap-2">
                  <Edit className="h-4 w-4" />
                  {language === 'ar' ? 'تحديث يدوي' : 'Manual Update'}
                </TabsTrigger>
                <TabsTrigger value="import" className="gap-2">
                  <Upload className="h-4 w-4" />
                  {language === 'ar' ? 'استيراد CSV' : 'Import CSV'}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="list">
                <PerformanceList
                  campaigns={campaigns}
                  loading={loading}
                  onUpdate={handleUpdatePerformance}
                />
              </TabsContent>

              <TabsContent value="import">
                <PerformanceImport
                  onImportComplete={fetchCampaigns}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPerformance;
